import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { FaEraser, FaPen, FaSearch } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { Template } from "../../../../components/template/template.jsx";
import { Card } from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import { normalizeFilterText } from "../../../../utils/normalizeFilterText.js";
import contactIdService from "../../../../services/web/contact-id-service.js";
import Button from "../../../../components/button/button.jsx";
import ModalEventTypes from "./components/modal-contact-id-event-types.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";

const initialFilter = {
    event: "",
    message: "",
    type: "",
    highPriorityEvents: false,
};

const ContactIdEventsList = () => {
    const gridRef = useRef(null);

    const [filters, setFilters] = useState(initialFilter);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const {data: queryData, isLoading, refetch} = useQuery({
        queryFn: async () => {
            const response = await contactIdService.getAllEventTypes();
            return response.data?.data ?? [];
        },
        queryKey: ["contact-id-event-types"]
    });

    useEffect(() => {
        if (!queryData?.length) return;

        setData(queryData);
        setFilteredData(queryData);
    }, [queryData]);

    const handleSearch = useCallback(() => {
        const {highPriorityEvents, ...textFilters} = filters;

        setFilteredData(
            data.filter(item => {
                const priority = Number(item.priority);
                const matchesPriority = !highPriorityEvents || (
                    item.priority !== null &&
                    item.priority !== undefined &&
                    item.priority !== "" &&
                    Number.isFinite(priority) &&
                    priority <= 2
                );
                const matchesText = Object.entries(textFilters).every(([key, filter]) => {
                    if (!filter) return true;

                    return normalizeFilterText(item[key] ?? "").includes(normalizeFilterText(filter));
                });

                return matchesPriority && matchesText;
            })
        );
    }, [data, filters]);

    const clearFilter = () => {
        setFilters(initialFilter);
        setFilteredData(data);
    };

    useEffect(() => {
        const handler = e => {
            if (e.key === "Enter") handleSearch();
            if (e.key === "Escape") clearFilter();
        };

        window.addEventListener("keyup", handler);
        return () => window.removeEventListener("keyup", handler);
    }, [handleSearch]);

    const columnDefs = useMemo(
        () => [
            { headerName: "Evento", field: "event", width: 120 },
            { headerName: "Mensagem", field: "message", sort: 'asc', sortingOrder: ["asc", "desc"], flex: 1,
                comparator: (valueA, valueB) => {
                    const a = normalizeFilterText(valueA || "");
                    const b = normalizeFilterText(valueB || "");

                    return a.localeCompare(b, 'pt-BR');
                }
            },
            { headerName: "Tipo", field: "type", width: 220 },
            { headerName: "", width: 50, sortable: false,
                cellRenderer: ({ data }) => (
                    <div className="flex items-center justify-center gap-1 h-full">
                        <Button
                            className={"w-7.5! h-6.25! p-0!"}
                            onClick={() => {
                                setSelectedEvent(data);
                                setModalOpen(true);
                            }}
                        >
                            <FaPen />
                        </Button>
                    </div>
                )
            },
        ],
        []
    );

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>Tipos de eventos</Template.Title>
                </Template.Header>

                <Template.Body>
                    <Card>
                        <Card.Body>
                            <div className="grid grid-cols-12 gap-4">
                                <InputForm
                                    label="Evento"
                                    value={filters.event}
                                    classNameWrapper="col-span-3"
                                    onChange={e =>
                                        setFilters(p => ({ ...p, event: e.target.value }))
                                    }
                                />

                                <InputForm
                                    label="Mensagem"
                                    value={filters.message}
                                    classNameWrapper="col-span-3"
                                    onChange={e =>
                                        setFilters(p => ({ ...p, message: e.target.value }))
                                    }
                                />

                                <InputForm
                                    label="Tipo"
                                    value={filters.type}
                                    classNameWrapper="col-span-3"
                                    onChange={e =>
                                        setFilters(p => ({ ...p, type: e.target.value }))
                                    }
                                />

                                <div className="flex gap-4 items-center col-span-3">
                                    <InputCheckbox
                                        className="flex-1"
                                        id="highPriorityEvents"
                                        label="Eventos com prioridade alta"
                                        checked={filters.highPriorityEvents}
                                        onChange={({target}) => setFilters(p => ({...p, highPriorityEvents: target.checked}))}
                                    />

                                    <div className={"flex gap-2"}>
                                        <Button target="primary-outline" onClick={clearFilter}>
                                            <FaEraser />
                                        </Button>

                                        <Button target="primary" onClick={handleSearch}>
                                            <FaSearch />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="mt-2 h-[calc(100vh-131px)]">
                        <Card.Body className="h-full">
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredData}
                                columnDefs={columnDefs}
                                suppressCellFocus
                                animateRows={false}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={theme}
                            />
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <ModalEventTypes open={modalOpen} setOpen={setModalOpen} event={selectedEvent} setEvent={setSelectedEvent} refetch={refetch}/>
        </>
    );
};

export default ContactIdEventsList;
